/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { DateInput, Form, Grid } from '@mairistem/composants';

import {
  DATE_FORMAT,
  URL_ETATS_MATRIMONIAUX_CONJOINT,
  regleEtatAnterieurConjoint,
} from '@/constants';
import { useOptionsReferentiel } from '@/hooks';

// Module autonome : propre `dayjs.extend`, ne pas dépendre d'un ordre d'import
// fortuit. Sans lui, `dayjs(valeur, DATE_FORMAT)` rend « Invalid Date » —
// silencieusement à l'écran, et de façon reproductible en test isolé.
dayjs.extend(customParseFormat);

interface ChampsEtatMatrimonialAnterieurProps {
  /**
   * Objet portant les champs (`etatMatrimonialAnterieureCode`,
   * `nomExConjoint`…) : le conjoint, et non la racine de `data`.
   */
  data: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (event: React.SyntheticEvent | null, payload: any) => void;
  /**
   * Préfixe des noms de champs ÉMIS (ex. `'conjoint1.'`), le point inclus.
   *
   * Dissocié de `data` : la lecture se fait sur l'objet déjà cadré, l'écriture
   * doit viser le chemin complet dans `data`. Les erreurs, elles, sont lues sans
   * préfixe — convention des validators (cf. `validationStep2`).
   */
  prefixe?: string;
  /** Charge le référentiel ; une modale ne l'active qu'à son ouverture. */
  actif?: boolean;
  /** Format des dates émises : l'assistant saisit en `DD/MM/YYYY`, la fiche en ISO. */
  formatDateSortie?: string;
}

/**
 * Champs de la situation ANTÉRIEURE d'un conjoint de mariage, affichés selon la
 * règle de la situation choisie (cf. `constants/etatMatrimonialMariage`).
 *
 * Quatre situations seulement, sur un référentiel qui lui est propre
 * (`M_EPX/EPSE`) : célibataire n'admet aucun champ complémentaire, les trois
 * autres portent une date dont le libellé varie et l'identité de l'ancien
 * conjoint — « partenaire » pour un Pacs.
 *
 * ⚠️ Pas de sexe d'ex-conjoint, contrairement au décès : `ec_actm` n'en a pas de
 * colonne.
 *
 * Partagé par l'assistant et la modale de la fiche : la règle vit en un seul
 * endroit, et l'API applique la même à l'écriture — un champ masqué après coup y
 * est purgé, l'IHM n'a donc pas à nettoyer elle-même.
 */
const ChampsEtatMatrimonialAnterieur = (
  props: ChampsEtatMatrimonialAnterieurProps,
) => {
  const {
    data,
    errors = {},
    onChange,
    prefixe = '',
    actif = true,
    formatDateSortie = DATE_FORMAT,
  } = props;

  const { options, items } = useOptionsReferentiel(
    actif ? URL_ETATS_MATRIMONIAUX_CONJOINT : undefined,
    'codeJustice',
  );

  const regle = regleEtatAnterieurConjoint(data?.etatMatrimonialAnterieureCode);

  /**
   * `am_ex_ematantl` est une dénormalisation du libellé : on la dérive de
   * l'option choisie. Les deux `onChange` s'enchaînent sans risque, l'écriture
   * passant par un `setData(prev => …)`.
   */
  const handleSituationChange = React.useCallback(
    (event: React.SyntheticEvent | null, { value }: { value: unknown }) => {
      onChange(event, {
        name: `${prefixe}etatMatrimonialAnterieureCode`,
        value,
      });

      const item = _.find(items, { codeJustice: value });

      onChange(null, {
        name: `${prefixe}etatMatrimonialAnterieureLibelle`,
        value: item?.libelle ?? null,
      });
    },
    [onChange, items, prefixe],
  );

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      onChange(null, {
        name: `${prefixe}dateEtatMatrimonial`,
        value: _.isNil(date) ? null : dayjs(date).format(formatDateSortie),
      });
    },
    [onChange, formatDateSortie, prefixe],
  );

  const role =
    regle.roleExConjoint === 'partenaire' ? 'ex-partenaire' : 'ex-conjoint';

  return (
    <Grid>
      <Grid.Row style={{ paddingLeft: '1em' }}>
        <Grid.Column computer={8} tablet={16} mobile={16}>
          <Form.Dropdown
            selection
            clearable
            fluid
            name={`${prefixe}etatMatrimonialAnterieureCode`}
            label="Etat matrimonial antérieur"
            placeholder="Choisir une situation"
            options={options}
            value={data?.etatMatrimonialAnterieureCode ?? ''}
            error={!_.isNil(errors?.etatMatrimonialAnterieureCode)}
            onChange={handleSituationChange}
          />
        </Grid.Column>
        {regle.libelleDate && (
          <Grid.Column computer={8} tablet={16} mobile={16}>
            <Form.Field error={!_.isNil(errors?.dateEtatMatrimonial)}>
              <label>{regle.libelleDate}</label>
              <DateInput
                value={
                  data?.dateEtatMatrimonial
                    ? dayjs(data.dateEtatMatrimonial, formatDateSortie).toDate()
                    : undefined
                }
                onChange={handleDateChange}
              />
            </Form.Field>
          </Grid.Column>
        )}
      </Grid.Row>
      {regle.roleExConjoint && (
        <Grid.Row style={{ paddingLeft: '1em' }}>
          <Grid.Column computer={8} tablet={8} mobile={16}>
            <Form.Input
              fluid
              label={`Nom ${role}`}
              name={`${prefixe}nomExConjoint`}
              // ec_actm.am_ex_nomex est un varchar(50).
              maxLength={50}
              value={data?.nomExConjoint ?? ''}
              onChange={onChange}
            />
          </Grid.Column>
          <Grid.Column computer={8} tablet={8} mobile={16}>
            <Form.Input
              fluid
              label={`Prénom ${role}`}
              name={`${prefixe}prenomExConjoint`}
              // ec_actm.am_ex_prenex est un varchar(100).
              maxLength={100}
              value={data?.prenomExConjoint ?? ''}
              onChange={onChange}
            />
          </Grid.Column>
        </Grid.Row>
      )}
    </Grid>
  );
};

export default ChampsEtatMatrimonialAnterieur;
